import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import TaskModal from "../components/TaskModal";
import { STATUS, PRIORITY, formatDate, daysUntil, initials } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

export default function TasksListPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [area, setArea] = useState("");
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const load = async () => {
    const [t, u, a, p] = await Promise.all([api.get("/tasks"), api.get("/users"), api.get("/areas"), api.get("/projects")]);
    setTasks(t.data); setUsers(u.data); setAreas(a.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const areaMap = Object.fromEntries(areas.map(a => [a.id, a.name]));
  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));

  const rows = tasks.filter(t => {
    if (status && t.status !== status) return false;
    if (priority && t.priority !== priority) return false;
    if (area && t.area_id !== area) return false;
    if (q && !((t.title||"") + " " + (t.description||"")).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const canCreate = user.role === "administrador" || user.role === "coordinador";
  const coordSinColabs = user.role === "coordinador" && users.filter(u => u.role === "colaborador").length === 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Tareas</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Vista de lista</h1>
        </div>
        {canCreate && (
          <button onClick={() => { setCurrent(null); setOpen(true); }} data-testid="new-task-btn" className="inline-flex items-center gap-2 bg-crejpp hover:bg-crejpp-dark text-white font-semibold rounded-md px-4 py-2 text-sm">
            <Plus className="w-4 h-4" /> Nueva tarea
          </button>
        )}
      </div>

      {coordSinColabs && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg px-4 py-3" data-testid="no-collabs-banner">
          No tiene colaboradores asignados. Comuníquese con el Administrador.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar tareas…" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="search-input" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md">
          <option value="">Todos los estados</option>
          {Object.entries(STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={priority} onChange={e => setPriority(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md">
          <option value="">Todas las prioridades</option>
          <option value="alta">Alta</option><option value="media">Media</option><option value="baja">Baja</option>
        </select>
        <select value={area} onChange={e => setArea(e.target.value)} className="text-sm px-3 py-2 border border-slate-300 rounded-md">
          <option value="">Todas las áreas</option>
          {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3 font-semibold">Tarea</th>
              <th className="px-4 py-3 font-semibold">Área</th>
              <th className="px-4 py-3 font-semibold">Responsable</th>
              <th className="px-4 py-3 font-semibold">Prioridad</th>
              <th className="px-4 py-3 font-semibold">Estado</th>
              <th className="px-4 py-3 font-semibold">Vence</th>
              <th className="px-4 py-3 font-semibold">Avance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(t => {
              const days = daysUntil(t.due_date);
              const overdue = t.status !== "cumplido" && days !== null && days < 0;
              const soon = t.status !== "cumplido" && days !== null && days <= 3 && days >= 0;
              const p = PRIORITY[t.priority]; const s = STATUS[t.status];
              return (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => { setCurrent(t); setOpen(true); }} data-testid={`task-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{t.title}</div>
                    {t.tags?.length > 0 && <div className="flex gap-1 mt-1">{t.tags.slice(0,3).map(tag => <span key={tag} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{tag}</span>)}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{areaMap[t.area_id] || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {t.assignee_id ? <>
                        <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-[10px] font-semibold flex items-center justify-center">{initials(userMap[t.assignee_id])}</div>
                        <span className="text-slate-700">{userMap[t.assignee_id]}</span>
                      </> : <span className="text-slate-400">Sin asignar</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${p.bg} ${p.text}`}>{p.label}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${s.bg} ${s.text}`}>{s.label}</span></td>
                  <td className={`px-4 py-3 ${overdue ? "text-crejpp font-semibold" : soon ? "text-amber-600 font-semibold" : "text-slate-600"}`}>
                    {overdue && <AlertCircle className="w-3 h-3 inline mr-1" />}
                    {formatDate(t.due_date)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[80px]">
                        <div className="h-full bg-crejpp" style={{ width: `${t.progress}%` }} />
                      </div>
                      <span className="text-xs text-slate-600">{t.progress}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-16 text-center text-slate-400">No hay tareas que coincidan con los filtros.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <TaskModal open={open} onClose={() => setOpen(false)} task={current} onSaved={load} areas={areas} users={users} projects={projects} />
    </div>
  );
}
